import React, { useState, useEffect } from 'react';
import { Plus, Trash2, X, Save } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';
import { Input } from './NewsManager';
import ImageUpload from './ImageUpload';

const emptyResult = { athlete: '', category: '', apparatus: '', medal: '' };
const emptyComp = () => ({ name: '', date: '', photos: [], results: [{ ...emptyResult }] });

const ResultsManager = () => {
  const [years, setYears] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ year: '', competitions: [emptyComp()] });

  const load = () => api.get('/results').then((r) => setYears(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing === 'new') await api.post('/results', form);
      else await api.put(`/results/${editing}`, { year: form.year, competitions: form.competitions });
      toast.success('Résultats enregistrés');
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer cette année ?')) return;
    await api.delete(`/results/${id}`); toast.success('Supprimé'); load();
  };

  const updateComp = (ci, k, v) => {
    const c = [...form.competitions]; c[ci] = { ...c[ci], [k]: v }; setForm({ ...form, competitions: c });
  };
  const updateRes = (ci, ri, k, v) => {
    const c = [...form.competitions]; const res = [...c[ci].results]; res[ri] = { ...res[ri], [k]: v };
    c[ci] = { ...c[ci], results: res }; setForm({ ...form, competitions: c });
  };
  const addComp = () => setForm({ ...form, competitions: [...form.competitions, emptyComp()] });
  const removeComp = (ci) => setForm({ ...form, competitions: form.competitions.filter((_, i) => i !== ci) });
  const addRes = (ci) => { const c = [...form.competitions]; c[ci].results.push({ ...emptyResult }); setForm({ ...form, competitions: c }); };
  const removeRes = (ci, ri) => { const c = [...form.competitions]; c[ci].results = c[ci].results.filter((_, i) => i !== ri); setForm({ ...form, competitions: c }); };

  const addPhoto = (ci) => {
    const c = [...form.competitions];
    const photos = c[ci].photos || [];
    if (photos.length >= 6) return;
    c[ci] = { ...c[ci], photos: [...photos, ''] };
    setForm({ ...form, competitions: c });
  };
  const updatePhoto = (ci, pi, v) => {
    const c = [...form.competitions];
    const photos = [...(c[ci].photos || [])];
    photos[pi] = v;
    c[ci] = { ...c[ci], photos };
    setForm({ ...form, competitions: c });
  };
  const removePhoto = (ci, pi) => {
    const c = [...form.competitions];
    c[ci] = { ...c[ci], photos: (c[ci].photos || []).filter((_, i) => i !== pi) };
    setForm({ ...form, competitions: c });
  };

  return (
    <div data-testid="results-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">Résultats des compétitions</h2>
        <button onClick={() => { setForm({ year: '', competitions: [emptyComp()] }); setEditing('new'); }} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-lg text-white font-medium"><Plus size={18} /> Ajouter une année</button>
      </div>

      <div className="grid gap-4">
        {years.map((y) => (
          <div key={y.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-fuchsia-400">{y.year}</h3>
              <div className="flex gap-2">
                <button onClick={() => { setForm(y); setEditing(y.id); }} className="px-3 py-1 text-sm text-white/70 hover:text-fuchsia-400">Modifier</button>
                <button onClick={() => remove(y.id)} className="p-2 text-white/60 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            </div>
            <p className="text-white/50 text-sm">{y.competitions.length} compétition(s)</p>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6 overflow-y-auto">
          <div className="bg-neutral-900 border border-white/10 rounded-2xl p-8 w-full max-w-3xl my-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Résultats {form.year}</h3>
              <button onClick={() => setEditing(null)} className="text-white/60 hover:text-white"><X size={24} /></button>
            </div>
            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
              <Input label="Année" value={form.year} onChange={(v) => setForm({ ...form, year: v })} placeholder="2026" />
              {form.competitions.map((comp, ci) => (
                <div key={ci} className="border border-white/10 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 items-end">
                    <div className="md:col-span-2"><Input label="Nom de la compétition" value={comp.name} onChange={(v) => updateComp(ci, 'name', v)} placeholder="Championnat Régional" /></div>
                    <div><Input label="Date (AAAA-MM-JJ)" value={comp.date || ''} onChange={(v) => updateComp(ci, 'date', v)} placeholder="2026-05-15" /></div>
                    <button type="button" onClick={() => removeComp(ci)} className="md:col-span-3 self-start text-red-400 text-sm">Supprimer cette compétition</button>
                  </div>
                  {comp.results.map((res, ri) => (
                    <div key={ri} className="grid grid-cols-12 gap-2 mb-2 items-center">
                      <input placeholder="Athlète" value={res.athlete} onChange={(e) => updateRes(ci, ri, 'athlete', e.target.value)} className="col-span-3 bg-white/5 border border-white/10 rounded py-2 px-2 text-white text-sm" />
                      <input placeholder="Catégorie" value={res.category} onChange={(e) => updateRes(ci, ri, 'category', e.target.value)} className="col-span-3 bg-white/5 border border-white/10 rounded py-2 px-2 text-white text-sm" />
                      <input placeholder="Engin" value={res.apparatus} onChange={(e) => updateRes(ci, ri, 'apparatus', e.target.value)} className="col-span-3 bg-white/5 border border-white/10 rounded py-2 px-2 text-white text-sm" />
                      <input placeholder="Rang (Or/Argent/Bronze/1/2/3/...)" value={res.medal || ''} onChange={(e) => updateRes(ci, ri, 'medal', e.target.value)} className="col-span-2 bg-white/5 border border-white/10 rounded py-2 px-2 text-white text-sm" />
                      <button onClick={() => removeRes(ci, ri)} className="col-span-1 text-white/60 hover:text-red-400"><X size={16} /></button>
                    </div>
                  ))}
                  <button onClick={() => addRes(ci)} className="text-fuchsia-400 text-sm flex items-center gap-1 mt-2"><Plus size={14} /> Ajouter un résultat</button>

                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white/70 text-sm">Photos de la compétition ({(comp.photos || []).length}/6)</span>
                      {(comp.photos || []).length < 6 && (
                        <button onClick={() => addPhoto(ci)} className="text-fuchsia-400 text-sm flex items-center gap-1"><Plus size={14} /> Ajouter une photo</button>
                      )}
                    </div>
                    {(comp.photos || []).map((photo, pi) => (
                      <div key={pi} className="flex items-start gap-2 mb-3">
                        <div className="flex-1"><ImageUpload value={photo} onChange={(v) => updatePhoto(ci, pi, v)} label={`Photo ${pi + 1}`} /></div>
                        <button onClick={() => removePhoto(ci, pi)} className="p-2 mt-6 text-white/60 hover:text-red-400"><X size={16} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <button onClick={addComp} className="text-fuchsia-400 text-sm flex items-center gap-1"><Plus size={16} /> Ajouter une compétition</button>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={save} data-testid="save-results-btn" className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-lg text-white font-medium"><Save size={18} /> Enregistrer</button>
              <button onClick={() => setEditing(null)} className="px-6 py-3 bg-white/5 border border-white/10 rounded-lg text-white/70">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultsManager;
